import { forwardRef, useEffect, useLayoutEffect, useRef, useState, MutableRefObject } from 'react'
import { styled } from '@mui/material/styles'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import FormHelperText from '@mui/material/FormHelperText'
import { useClickAway } from 'react-use'
import Quill from 'quill'
import { EmitterSource } from 'quill/core/emitter'
import { Range } from 'quill/core/selection'
import Delta, { Op } from 'quill-delta'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'

const QuillStyled = styled('div')<{
  isHover: boolean
  isEditing: boolean
  hasLabel?: boolean
  variant: 'filled' | 'outlined' | 'text'
}>(({ theme, isHover, isEditing, hasLabel, variant }) => ({
  '.ql-toolbar':
    variant === 'text'
      ? {}
      : {
          borderRadius: hasLabel ? 0 : '0.5rem 0.5rem 0 0',
          borderWidth: variant === 'filled' ? '0.1rem 0' : '0.15rem',
          borderColor: theme.colors.secondary,
          borderStyle: 'solid',
          backgroundColor:
            variant === 'filled'
              ? theme.colors.secondaryBackground
              : theme.palette.background.paper,
          ...(isHover
            ? {
                borderColor: variant === 'outlined' ? 'rgb(0, 0, 0)' : undefined,
              }
            : {}),
          ...(isEditing
            ? {
                borderColor: variant === 'outlined' ? theme.palette.primary.main : undefined,
              }
            : {}),
        },
  '.ql-container':
    variant === 'text'
      ? {
          fontFamily: theme.typography.fontFamily,
          fontSize: '1rem',
        }
      : {
          fontFamily: theme.typography.fontFamily,
          fontSize: '1rem',
          borderRadius: variant === 'filled' ? '0' : '0 0 0.5rem 0.5rem',
          borderWidth: variant === 'filled' ? '0rem' : '0.15rem',
          borderColor: theme.colors.secondary,
          borderStyle: 'solid',
          borderBottom:
            variant === 'filled' ? `0.1rem solid ${theme.palette.text.primary}` : undefined,
          ...(isHover
            ? {
                borderColor: variant === 'outlined' ? 'rgb(0, 0, 0)' : undefined,
              }
            : {}),
          ...(isEditing
            ? {
                borderBottom:
                  variant === 'filled' ? `0.2rem solid ${theme.palette.primary.main}` : undefined,
                borderColor: variant === 'outlined' ? theme.palette.primary.main : undefined,
              }
            : {}),
        },
}))

interface Props {
  readOnly?: boolean
  name?: string
  defaultValue?: string
  onTextChange?: (delta: Delta, oldContent: Delta, source: EmitterSource) => void
  onSelectionChange?: (range: Range, oldRange: Range, source: EmitterSource) => void
  label?: React.ReactNode
  helperText?: string
  variant?: 'filled' | 'outlined' | 'text'
  theme?: 'snow' | 'bubble'
}

export type QuillEditorRef = Quill

const toolbarOptions = [
  [{ font: [] }, { header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike'], // toggled buttons
  [{ color: [] }, { background: [] }], // dropdown with defaults from theme

  [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
  [{ indent: '-1' }, { indent: '+1' }], // outdent/indent

  // [{ size: [] }], // custom dropdown

  [{ align: [] }, { direction: 'rtl' }],

  [{ script: 'sub' }, { script: 'super' }, 'formula'], // superscript/subscript
  ['blockquote', 'code-block', 'link', 'image', 'video'],

  ['clean'], // remove formatting button
]

const QuillEditor = forwardRef<QuillEditorRef, Props>(
  (
    {
      label,
      name,
      helperText,
      defaultValue,
      onTextChange,
      onSelectionChange,
      readOnly = false,
      variant = 'outlined',
      theme = 'snow',
    },
    ref
  ) => {
    const quillRef = ref as MutableRefObject<QuillEditorRef | null>
    const formControlRef = useRef(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const defaultValueRef = useRef(
      (() => {
        let safeJson = [] as Op[]
        if (defaultValue) {
          try {
            safeJson = JSON.parse(defaultValue) as Op[]
          } catch (error) {
            console.error(error)
          }
        }
        return safeJson
      })()
    )
    const onTextChangeRef = useRef(onTextChange)
    const onSelectionChangeRef = useRef(onSelectionChange)

    const [isHover, setIsHover] = useState(false)
    const [isEditing, setIsEditing] = useState(false)

    useClickAway(formControlRef, () => {
      setIsEditing(false)
    })

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange
      onSelectionChangeRef.current = onSelectionChange
    })

    useEffect(() => {
      quillRef.current?.enable(!readOnly)
    }, [quillRef, readOnly])

    useEffect(() => {
      const container = containerRef.current
      const editorContainer = container!.appendChild(container!.ownerDocument.createElement('div'))
      const quill = new Quill(editorContainer, {
        theme,
        readOnly,
        modules: {
          toolbar: toolbarOptions,
        },
      })

      quillRef.current = quill

      if (defaultValueRef.current) {
        quill.setContents(defaultValueRef.current)
      }

      quill.on(Quill.events.TEXT_CHANGE, (...args) => {
        onTextChangeRef.current?.(...args)
      })

      quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
        onSelectionChangeRef.current?.(...args)
      })

      return () => {
        quillRef.current = null
        if (container) container.innerHTML = ''
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quillRef])

    return (
      <FormControl
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        sx={theme =>
          variant === 'text'
            ? {}
            : {
                borderRadius: !label ? 0 : '0.5rem 0.5rem 0 0',
                backgroundColor:
                  variant === 'filled'
                    ? theme.palette.background.default
                    : theme.palette.background.paper,
              }
        }
        ref={formControlRef}
      >
        {label && (
          <FormLabel
            sx={theme => ({
              fontSize: '0.85rem !important',
              fontWeight: 400,
              padding: '0.5rem 0.7rem',
              backgroundColor:
                variant === 'filled'
                  ? theme.palette.background.default
                  : theme.palette.background.paper,
              borderRadius: '0.5rem 0.5rem 0 0',
              borderWidth: variant === 'filled' ? '0rem' : '0.15rem',
              borderColor: theme.colors.secondary,
              borderStyle: 'solid',
              borderBottomWidth: 0,
              ...(isHover
                ? {
                    backgroundColor: variant === 'filled' ? 'rgba(0, 0, 0, 0.09)' : undefined,
                    borderColor: variant === 'outlined' ? 'rgb(0, 0, 0)' : undefined,
                  }
                : {}),
              ...(isEditing
                ? {
                    borderColor: variant === 'outlined' ? theme.palette.primary.main : undefined,
                  }
                : {}),
            })}
            htmlFor={name}
          >
            {label}
          </FormLabel>
        )}
        <QuillStyled
          onClick={() => {
            setIsEditing(true)
          }}
          isEditing={isEditing}
          isHover={isHover}
          variant={variant}
          hasLabel
          ref={containerRef}
        />
        {helperText && (
          <FormHelperText id={`${name ?? 'quill'}-helper-text`}>{helperText}</FormHelperText>
        )}
      </FormControl>
    )
  }
)

QuillEditor.displayName = 'QuillEditor'

export default QuillEditor
